import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DesktopDatePicker, DesktopDateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { abortController } from '../../utils/abortController';
import useUser from '../../hooks/useUser';
import useSessionExpire from '../../hooks/useSessionExpire';
import DealSBLC from './DealSBLC';
import TextField from '@mui/material/TextField';
import AddBoxIcon from '@mui/icons-material/AddBox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import Grid from '@mui/material/Grid';
import VirtualizedAutocomplete from '../../components/Autocomplete';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import IconButton from '@mui/material/IconButton';
import CustomTextMask from '../../components/CustomTextMask';
import dayjs from 'dayjs';
import axios from 'axios';
import { isFieldLocked } from '../../utils/fieldUtils';
import { InputAdornment, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import useTab from '../../hooks/useTab';

const DealHeader = forwardRef(({ isWritable = false, listData = {}, isListDataLoading = false, isFertilizersAndPetroChemicalsDeal = false, isFoodsOrPolymersDeal = false, isFoodsPolymersOrSBLCDeal = false, isSBLCDeal = false, isExistingDealHeader = false, isExistingSBLC = false, isFormSavedOrDeleted = false, selectedBusinessUnit = null, selectedDeal = null, isLinkedThrougRoute = false, selectedPFI = null, selctedPFIForAutoPopulate = null, formData = {}, productData = [], customerCounterParties = [], supplierCounterParties = [], isPeriodLocked = false, lockedFields = [], sellingLegFormData = {}, sblcInitialFormData = {}, sblcFormData = {}, expandedFormSections = [], setIsExistingDealHeader = () => { }, setCustomerCounterParties = () => { }, setSupplierCounterParties = () => { }, resetLockedFields = () => { }, setIsExistingSBLC = () => { }, setIsFormUpdated = () => { }, setIsSBLCFormUpdated = () => { }, setIsFormSavedOrDeleted = () => { }, setFormData = () => { }, setSellingLegFormData = () => { }, setSBLCFormData = () => { }, setExpandedFormSections = () => { }, setWarningDialogText = () => { }, setShowWarningDialog = () => { }, setShowDeleteModal = () => { }, setShowAlert = () => { }, setAlertMessage = () => { }, setAlertType = () => { }, logOut = () => { } }, ref) => {

  const {
    setIsNewPFI = () => { },
  } = useTab();
  const { pathname } = useLocation();

  /* LIST VARIABLES */
  const {
    vendors = []
  } = listData;

  /* AUTHENTICATION VARIABLES */
  const currentUser = useUser();

  /* SESSION EXPIRY VARIABLES */
  const triggerSessionExpire = useSessionExpire();

  /* LIST VARIABLES */
  const [products, setProducts] = useState([]);
  const [productGrades, setProductGrades] = useState([]);

  const orderStatus = useMemo(() => ["In-Progress", "Cancelled", "Closed"], []);
  const transactionTypes = useMemo(() => ["Addition", "Subtraction"], []);
  const tradeFinanceDealOptions = useMemo(() => ["Yes", "No"], []);

  /* FORM VARIABLES */
  const formRef = useRef(null);

  /* INPUT REFERENCE VARIABLES */
  const freightInputRef = useRef(null);
  const fobValueInputRef = useRef(null);

  /* ACCORDIAN VARIABLES */
  const [isProductAccordionExpanded, setIsProductAccordionExpanded] = useState(null);
  const [isPFIAccordionExpanded, setIsPFIAccordionExpanded] = useState(null);
  const [isProductGradeAccordionExpanded, setIsProductGradeAccordionExpanded] = useState(null);
  const productAccordionRefs = useRef([]);
  const pfiAccordionRefs = useRef([]);
  const productGradeAccordionRefs = useRef([]);
  const prevProductCountRef = useRef(formData?.products?.length || 0);


  useEffect(() => {
    const fetchLists = async () => {

      axios({
        method: "post",
        url: "/api/GetProductGradeList",
        data: {
          pathname: pathname,
          Business_Unit_Id: selectedBusinessUnit
        },
        signal: abortController.signal
      })
        .then((response) => {
          const { status, data } = response;

          if (status === 200) {
            const productGrades = data?.product_grades || [];
            setProductGrades(productGrades);
          }
        })
        .catch((error) => {
          console.log("Get Product Grades Api: ", error);

          if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
            return;
          }

          const status = error?.response?.status;

          if (status === 403) {
            triggerSessionExpire();
          }
          else {
            setAlertMessage(
              status === 401
                ? "Unauthorized access. You do not have the required permissions to perform this action."
                : status === 429
                  ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                  : "An error occurred while processing your request. Please try again later or contact the site administrator."
            );
            setAlertType("error");
            setShowAlert(true);

            if (status === 429) {
              setTimeout(logOut, 3000);
            }
          }
        });

      axios({
        method: "post",
        url: "/api/GetProductList",
        data: {
          pathname: pathname,
          Business_Unit_Id: selectedBusinessUnit
        },
        signal: abortController.signal
      })
        .then((response) => {
          const { status, data } = response;

          if (status === 200) {
            const products = data?.product || [];
            setProducts(products);
          }
        })
        .catch((error) => {
          console.log("Get Filtered Products Api: ", error);

          if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
            return;
          }

          const status = error?.response?.status;

          if (status === 403) {
            triggerSessionExpire();
          }
          else {
            setAlertMessage(
              status === 401
                ? "Unauthorized access. You do not have the required permissions to perform this action."
                : status === 429
                  ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                  : "An error occurred while processing your request. Please try again later or contact the site administrator."
            );
            setAlertType("error");
            setShowAlert(true);

            if (status === 429) {
              setTimeout(logOut, 3000);
            }
          }
        });

      axios({
        method: "post",
        url: "/api/GetCounterPartyList",
        data: {
          pathname: pathname,
          Business_Unit_Id: selectedBusinessUnit,
          Deal_PFI_Id: selectedPFI
        },
        signal: abortController.signal
      })
        .then((response) => {
          const { status, data } = response;

          if (status === 200) {
            const { customer_vendors: customerCounterParties = [], supplier_vendors: supplierCounterParties = [] } = data || {};

            setCustomerCounterParties(customerCounterParties);
            setSupplierCounterParties(supplierCounterParties);
          }
        })
        .catch((error) => {
          console.log("Get Filtered Sellers/Counter Parties Api: ", error);

          if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
            return;
          }

          const status = error?.response?.status;

          if (status === 403) {
            triggerSessionExpire();
          }
          else {
            setAlertMessage(
              status === 401
                ? "Unauthorized access. You do not have the required permissions to perform this action."
                : status === 429
                  ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                  : "An error occurred while processing your request. Please try again later or contact the site administrator."
            );
            setAlertType("error");
            setShowAlert(true);

            if (status === 429) {
              setTimeout(logOut, 3000);
            }
          }
        });
    }

    fetchLists();

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    const currentCount = formData?.products?.length || 0;
    const prevCount = prevProductCountRef.current;

    // Only expand accordion if a new product is added
    if (currentCount > prevCount) {
      setIsProductAccordionExpanded(currentCount - 1); // expand newly added product
    }

    // Update previous count
    prevProductCountRef.current = currentCount;
  }, [formData?.products]);





  useEffect(() => {
    if (isSBLCDeal) {
      // Check if the 'sblcFormData' is empty
      const isEmpty = typeof sblcFormData === "object" && !sblcFormData?.Deal_PFI_Id;

      // Set existing deal sblc flags
      if (!isEmpty && !selctedPFIForAutoPopulate) {
        setIsExistingSBLC(true);
      }
      else {
        setIsExistingSBLC(false);
      }

      if (!isFormSavedOrDeleted && !selctedPFIForAutoPopulate) {
        const isSBLCFormUpdated = checkIfFormUpdated(sblcFormData, sblcInitialFormData);
        setIsSBLCFormUpdated(isSBLCFormUpdated);
      }
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isSBLCDeal, sblcFormData, selctedPFIForAutoPopulate]);

  useImperativeHandle(ref, () => ({
    isValid,
    handleSubmit: handleSubmit,
    handleDelete: handleDelete
  }));

  const checkIfFormUpdated = (formData, initialFormData, dateKeys = []) => {
    // Check non-date fields
    const isNonDateUpdated = Object.keys(initialFormData).some((key) => {
      const currentValue = formData[key];
      const initialValue = initialFormData[key];

      return JSON.stringify(currentValue) !== JSON.stringify(initialValue);
    });

    // Check date fields separately
    const isDateUpdated = dateKeys.some((dateKey) => {
      const currentDate = formData[dateKey] ? dayjs(formData[dateKey]) : formData[dateKey];
      const initialDate = initialFormData[dateKey] ? dayjs(initialFormData[dateKey]) : initialFormData[dateKey];

      return JSON.stringify(currentDate) !== JSON.stringify(initialDate);
    });

    return isNonDateUpdated || isDateUpdated;
  }

  const convertToDayJsObject = (date, datetime) => {
    const convertedDate = date ? dayjs(date, "MM/DD/YYYY") : datetime ? dayjs(datetime, "MM/DD/YYYY hh:mm A") : null;
    return convertedDate;
  }

  const shouldDisableDate = (date, type) => {
    if (!formData?.Booked_for_Month) {
      return false;
    }

    const selectedDate = new Date(date);
    const bookedDate = new Date(formData?.Booked_for_Month);

    return (
      selectedDate.getFullYear() !== bookedDate.getFullYear() ||
      (type === "Month" && selectedDate.getMonth() !== bookedDate.getMonth())
    );
  }

  const isValid = () => {
    return formRef.current?.reportValidity();
  }

  const handleFormSectionAccordionChange = (section) => (event, isExpanded) => {
    setExpandedFormSections(isExpanded ? [...expandedFormSections, section] : expandedFormSections.filter(id => id !== section));
  }

  const handleAccordionChange = (index) => (event, isExpanded) => {
    setIsProductAccordionExpanded(isExpanded ? index : null);
  }

  const handleChange = (event, field, value, isDate = false) => {
    let newValue = value;

    if (isDate && newValue) {
      newValue = dayjs(newValue).format("MM/DD/YYYY");
    }

    if (["Freight", "FOB_Value", "Value"].includes(field)) {
      newValue = value.replace(/[^0-9.]/g, "");
      if (newValue.includes(".")) {
        const [integerPart, decimalPart] = newValue.split(".");
        newValue = `${integerPart}.${decimalPart.substring(0, 3)}`;
      }
    }

    const getSanitizedValue = (value) => typeof value === "string" ? (value.trim() === "" ? null : value) : value ?? null;
    const updates = { [field]: getSanitizedValue(newValue) };
    if (field === 'Buyer_Id') {
      const tempVendor = vendors;
      const matchedVendor = tempVendor?.find(v => v.id === newValue?.id);
      if (matchedVendor?.Address) {
        setSellingLegFormData(prev => {
          const updatedData = {
            ...prev,
            Customer_Address: matchedVendor?.Address,
          };
          // If Ship to Party is same as customer, update ship to party fields as well
          if (prev.Ship_To_Party_Same_As_Customer === "Yes") {
            updatedData.Ship_To_Party_Id = newValue;
            updatedData.Ship_To_Party_Address = matchedVendor?.Address;
          }
          return updatedData;
        });
      }
    }
    if (["Customer_Address", "Ship_To_Party_Same_As_Customer", "Ship_To_Party_Id", "Ship_To_Party_Address"].includes(field)) {
      if (field === "Ship_To_Party_Same_As_Customer") {
        if (updates[field] === "Yes") {
          // Auto-select the customer as ship to party
          updates.Ship_To_Party_Id = formData?.Buyer_Id;
          updates.Ship_To_Party_Address = sellingLegFormData?.Customer_Address;
        } else {
          // User selected No, so clear existing ship-to party details so they can select a new party
          updates.Ship_To_Party_Id = null;
          updates.Ship_To_Party_Address = null;
        }
      }
      else if (field === "Ship_To_Party_Id") {
        const matchedVendor = vendors?.find(v => v.id === newValue?.id);
        const shipToPartyAddress = matchedVendor?.Address || newValue?.Address;
        if (shipToPartyAddress) {
          updates.Ship_To_Party_Address = shipToPartyAddress;
        }
      }

      setSellingLegFormData((previousSellingLegFormData) => ({
        ...previousSellingLegFormData,
        ...updates
      }));
    } else {
      if (field === "Booked_for_Month") {
        updates.Order_Date = null;
      }
      else if (field === "Deal_Close_Date") {
        updates.Order_Status = orderStatus.find((status) => status === "Closed") || null;
      }
      else if (field === "Freight" || field === "FOB_Value") {
        freightInputRef?.current?.setCustomValidity("");
        fobValueInputRef?.current?.setCustomValidity("");

        // Use parseFloat for decimals
        const freight = field === "Freight" ? parseFloat(newValue || 0) : parseFloat(formData["Freight"] || 0);
        const fobValue = field === "FOB_Value" ? parseFloat(newValue || 0) : parseFloat(formData["FOB_Value"] || 0);
        let calculatedTotalAmount = freight + fobValue;
        // Optional: Enforce precision
        calculatedTotalAmount = parseFloat(calculatedTotalAmount.toFixed(3));

        const actualTotalAmount = parseFloat(formData["Total_Amount"] || 0);

        if (calculatedTotalAmount !== actualTotalAmount) {
          event?.target?.setCustomValidity(
            `The calculated total is ${calculatedTotalAmount > actualTotalAmount ? "more" : "less"} than the actual total amount.`
          );
        }
      }

      setFormData((previousFormData) => ({ ...previousFormData, ...updates }));
    }
  }

  const handleProductChange = (index, field, value) => {

    let formattedValue = value;
    if (["Quantity", "Buying_Price", "Selling_Price"].includes(field)) {
      // Remove any non-numeric characters except the decimal point
      formattedValue = value.replace(/[^0-9.]/g, "");
      if (formattedValue.includes(".")) {
        const [integerPart, decimalPart] = formattedValue.split(".");
        if (field === "Quantity") {
          // Ensure the value has at most 3 decimal places
          formattedValue = `${integerPart}.${decimalPart.substring(0, 3)}`;
        }
        else if (["Buying_Price", "Selling_Price"].includes(field)) {
          // Ensure the value has at most 3 decimal places, round if more
          if (decimalPart.length > 3) {
            formattedValue = parseFloat(`${integerPart}.${decimalPart}`).toFixed(3);
          }
          else {
            formattedValue = `${integerPart}.${decimalPart.substring(0, 3)}`;
          }
        }
      }
    }
    const data = { ...formData };
    const newProducts = [...data.products];
    const updatedProduct = { ...newProducts[index] };
    // Get old quantity & price before update
    const oldQuantity = parseFloat(newProducts[index]?.Quantity || 0);
    const oldPrice = parseFloat(newProducts[index]?.Selling_Price || 0);
    const oldAmount = oldQuantity * oldPrice;
    updatedProduct[field] = formattedValue;
    newProducts[index] = updatedProduct;
    // Get new quantity & price after update
    const newQuantity = parseFloat(updatedProduct?.Quantity || 0);
    const newPrice = parseFloat(updatedProduct?.Selling_Price || 0);
    let newAmount = newQuantity * newPrice;
    // Round Total_Amount to 3 decimal places if it has more
    if ((newAmount.toString().split(".")[1]?.length || 0) > 3) {
      newAmount = parseFloat(newAmount.toFixed(3));
    }
    // Adjust total by subtracting old and adding new
    let totalAmount = (data["Total_Amount"] || 0) - oldAmount + newAmount;
    // Round Total_Amount to 3 decimal places if it has more
    if ((totalAmount.toString().split(".")[1]?.length || 0) > 3) {
      totalAmount = parseFloat(totalAmount.toFixed(3));
    }
    setFormData((previousFormData) => ({
      ...previousFormData,
      products: newProducts,
      Total_Amount: totalAmount,
      ...(["Quantity", "Selling_Price"].includes(field) ? { Freight: null, FOB_Value: null } : {})
    }));
  }

  const handleAddProduct = (event) => {
    event.stopPropagation();

    const products = [...formData.products || []];
    const isAnyProductFieldFilled =
      products.length > 0
        ? products.every((product) =>
          Object.values(product).some((value) =>
            Array.isArray(value) ? value.length > 0 : Boolean(value)
          )
        )
        : true;

    if (isAnyProductFieldFilled) {
      const newProductData = {
        Product_Id: null,
        Unit_of_Measurement: "",
        Product_Grade_Id: null,
        Product_Specification: "",
        Product_Description: "",
        Quantity: "",
        Buying_Price: "",
        Selling_Price: "",
        HS_Code: ""
      };
      const updatedProducts = [...products, newProductData];

      setFormData((previousFormData) => ({
        ...previousFormData,
        products: updatedProducts,
        Freight: null,
        FOB_Value: null
      }));
    }
    else {
      setWarningDialogText("Please fill in at least one field in the current product details before adding a new one.");
      setShowWarningDialog(true);
    }
  }

  const handleDeleteProduct = (event, index) => {
    event.stopPropagation();
    setFormData((previousFormData) => {
      const productToDelete = previousFormData.products[index];
      const deletedAmount = (parseFloat(productToDelete?.Quantity || 0) * parseFloat(productToDelete?.Selling_Price || 0)) || 0;
      let totalAmount = (previousFormData.Total_Amount || 0) - deletedAmount;
      // Round Total_Amount to 3 decimal places if it has more
      if ((totalAmount.toString().split(".")[1]?.length || 0) > 3) {
        totalAmount = parseFloat(totalAmount.toFixed(3));
      }
      return {
        ...previousFormData,
        products: previousFormData.products.filter((_, i) => i !== index),
        Total_Amount: totalAmount,
        Freight: null,
        FOB_Value: null
      };
    });
  }
  const handleSubmit = async (event, data, pfiLockStatus, isExistingDealHeader) => {
    event?.preventDefault();

    if (!isValid()) {
      return false;
    }

    return new Promise((resolve, reject) => {
      setShowWarningDialog(false);
      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");

      let updatedFormData = {
        ...data,
        Business_Unit_Id: selectedBusinessUnit,
        Deal_PFI_Id: data?.Deal_PFI_Id || selectedPFI,
        User_Id: currentUser?.id
      };

      if (updatedFormData?.products?.length > 0 && selctedPFIForAutoPopulate) {
        updatedFormData.products = updatedFormData?.products?.map(product => {
          const { Deal_Product_Id, ...rest } = product;
          return rest;
        });
      }

      updatedFormData = Object.fromEntries(Object.entries(updatedFormData).map(([key, value]) => [
        key,
        typeof value === "object" && value?.hasOwnProperty("value") ? value.id : value
      ]));

      if (pfiLockStatus === 1) {
        updatedFormData = resetLockedFields(updatedFormData, lockedFields, 'Deal Header');
      }

      const apiUrl = isExistingDealHeader
        ? isFertilizersAndPetroChemicalsDeal
          ? "/api/UpdateOrderDetailsFertilizersAndPetroChemicals"
          : isFoodsPolymersOrSBLCDeal
            ? "/api/UpdateOrderDetailsFoodsPolymersAndSBLC"
            : ""
        : isFertilizersAndPetroChemicalsDeal
          ? "/api/AddOrderDetailsFertilizersAndPetroChemicals"
          : isFoodsPolymersOrSBLCDeal
            ? "/api/AddOrderDetailsFoodsPolymersAndSBLC"
            : "";
      let isSBLCSaved = Promise.resolve();

      if (isSBLCDeal) {
        let updatedSBLCFormData = {
          ...sblcFormData,
          Deal_PFI_Id: selectedPFI,
          User_Id: currentUser?.id
        };
        const sblcApiUrl = isExistingSBLC ? "/api/UpdateDealSBLCProductGrades" : "/api/AddDealSBLCProductGrades";

        const transformObject = (obj, seen = new Set()) => {
          if (seen.has(obj)) return obj; // Return the object if it has already been processed
          if (typeof obj !== "object" || obj === null) return obj; // Base case for non-objects

          seen.add(obj); // Add the current object to the seen set

          if (Array.isArray(obj)) {
            return obj.map(item =>
              typeof item === "object" && item !== null
                ? item.hasOwnProperty("value")
                  ? item.id
                  : transformObject(item, seen) // Recursively transform nested arrays
                : item
            );
          }

          return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [
              key,
              typeof value === "object" && value !== null
                ? Array.isArray(value)
                  ? transformObject(value, seen)  // Handle arrays
                  : value.hasOwnProperty("value")
                    ? value.id
                    : transformObject(value, seen)  // Recursively transform nested objects
                : value
            ])
          );
        }


        if (pfiLockStatus === 1) {
          updatedSBLCFormData = resetLockedFields(updatedSBLCFormData, lockedFields, "SBLC Product Grade");
        }

        updatedSBLCFormData = transformObject(updatedSBLCFormData);

        isSBLCSaved = axios({
          method: "post",
          url: sblcApiUrl,
          data: updatedSBLCFormData,
          signal: abortController.signal
        })
          .then((response) => {
            const { status, data } = response;

            if (status === 400) {
              setAlertMessage(data?.error || "An error occurred while processing your request. Please try again later or contact the site administrator.");
              setAlertType("error");
              setShowAlert(true);
              reject(data?.error);
            }
            else if (status !== 200) {
              setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
              setAlertType("error");
              setShowAlert(true);
              reject(false);
            }
          })
          .catch((error) => {
            console.log("Add/Update Deal SBLC Product Grade Data Api: ", error);

            if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
              return;
            }

            const status = error?.response?.status;

            if (status === 403) {
              triggerSessionExpire();
            }
            else {
              setAlertMessage(
                status === 401
                  ? "Unauthorized access. You do not have the required permissions to perform this action."
                  : status === 429
                    ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                    : "An error occurred while processing your request. Please try again later or contact the site administrator."
              );
              setAlertType("error");
              setShowAlert(true);

              if (status === 429) {
                setTimeout(logOut, 3000);
              }
            }

            reject(error);
          });
      }

      isSBLCSaved
        .then(() =>
          axios.post(apiUrl, updatedFormData, { signal: abortController.signal })
            .then((response) => {
              const { status, data } = response;

              if (status === 200) {
                setIsFormUpdated(false);
                setIsFormSavedOrDeleted(true);
                resolve(true);
                setIsNewPFI(false)
              }
              else {
                const errorMessage = status === 400
                  ? (data?.error || "An error occurred while processing your request. Please try again later or contact the site administrator.")
                  : "An error occurred while processing your request. Please try again later or contact the site administrator.";

                setAlertMessage(errorMessage);
                setAlertType("error");
                setShowAlert(true);
                reject(errorMessage);
              }
            }))
        .catch((error) => {
          console.log("Add/Update Deal Details Api: ", error);

          if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
            return;
          }

          const status = error?.response?.status;

          if (status === 403) {
            triggerSessionExpire();
          }
          else {
            setAlertMessage(
              status === 401 ? "Unauthorized access. You do not have the required permissions." :
                status === 429 ? "Request limit exceeded. Your account is temporarily disabled." :
                  "An error occurred while processing your request. Please try again later or contact the site administrator."
            );
            setAlertType("error");
            setShowAlert(true);

            if (status === 429) {
              setTimeout(logOut, 3000);
            }
          }

          reject(error);
        });
    });
  }

  const handleDelete = async (passedPFI_Id) => {


    return new Promise((resolve, reject) => {
      setShowDeleteModal(false);
      setShowAlert(false);
      setAlertMessage("");
      setAlertType("");

      let isSBLCDeleted = Promise.resolve();

      if (isSBLCDeal) {
        isSBLCDeleted = axios({
          method: "post",
          url: "/api/DeleteDealSBLCProductGrades",
          data: {
            Deal_PFI_Id: passedPFI_Id,
            SBLC_Id: sblcFormData?.SBLC_Id?.id,
            User_Id: currentUser?.id
          },
          signal: abortController.signal
        })
          .then((response) => {
            const { status, data } = response;

            if (status === 200) {
              setIsExistingSBLC(false);
              setIsSBLCFormUpdated(false);
              setIsPFIAccordionExpanded(null);
              setIsProductGradeAccordionExpanded(null);
            }
            else if (status === 400) {
              setAlertMessage(data?.error || "An error occurred while processing your request. Please try again later or contact the site administrator.");
              setAlertType("error");
              setShowAlert(true);
              reject(false);
            }
            else {
              setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
              setAlertType("error");
              setShowAlert(true);
              reject(false);
            }
          })
          .catch((error) => {
            console.log("Delete Buying Leg SBLC Api: ", error);

            if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
              return;
            }

            const status = error?.response?.status;

            if (status === 403) {
              triggerSessionExpire();
            }
            else {
              setAlertMessage(
                status === 401
                  ? "Unauthorized access. You do not have the required permissions to perform this action."
                  : status === 429
                    ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                    : "An error occurred while processing your request. Please try again later or contact the site administrator."
              );
              setAlertType("error");
              setShowAlert(true);

              if (status === 429) {
                setTimeout(logOut, 3000);
              }
            }

            reject(false);
          });
      }

      isSBLCDeleted
        .then(() =>
          axios.post("/api/DeleteOrderDetails", {
            Business_Unit_Id: selectedBusinessUnit,
            Deal_PFI_Id: passedPFI_Id,
            User_Id: currentUser?.id
          }, {
            signal: abortController.signal
          })
            .then((response) => {
              const { status, data } = response;

              if (status === 200) {
                formRef?.current?.reset();

                setIsExistingDealHeader(false);
                setIsFormUpdated(false);
                setIsFormSavedOrDeleted(true);
                resolve(true);
              }
              else if (status === 400) {
                setAlertMessage(data?.error || "An error occurred while processing your request. Please try again later or contact the site administrator.");
                setAlertType("error");
                setShowAlert(true);
                reject(false);
              }
              else {
                setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                setAlertType("error");
                setShowAlert(true);
                reject(false);
              }
            }))
        .catch((error) => {
          console.log("Delete Deal Header Data Api: ", error);

          if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
            return;
          }

          const status = error?.response?.status;

          if (status === 403) {
            triggerSessionExpire();
          }
          else {
            setAlertMessage(
              status === 401
                ? "Unauthorized access. You do not have the required permissions to perform this action."
                : status === 429
                  ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                  : "An error occurred while processing your request. Please try again later or contact the site administrator."
            );
            setAlertType("error");
            setShowAlert(true);

            if (status === 429) {
              setTimeout(logOut, 3000);
            }
          }

          reject(false);
        });
    });
  }

  const formSectionsHTML = [
    {
      id: 1,
      columns: 12,
      title: "Deal Information",
      content: (
        <Grid
          container
          spacing={"10px"}
          className="align-items-start autocomplete"
        >
          <Grid size={{ xs: 3 }}>
            <VirtualizedAutocomplete
              isMultiple={false}
              isObject={false}
              isWritable={isWritable}
              isLocked={Boolean(isPeriodLocked && isFieldLocked("Order_Status", "Deal Header", "Pre-Shipment", lockedFields))}
              filterOn="Deal Status"
              options={orderStatus}
              selectedOptions={formData?.Order_Status || null}
              handleSelectChange={(filterOn, newValue) => handleChange(null, "Order_Status", newValue)}
            />
          </Grid>

          <Grid size={{ xs: 3 }}>
            <VirtualizedAutocomplete
              isLoading={isListDataLoading}
              isMultiple={false}
              isObject={true}
              isWritable={isWritable}
              isLocked={Boolean(isPeriodLocked && isFieldLocked("Seller_Id", "Deal Header", "Pre-Shipment", lockedFields))}
              filterOn="Supplier Name"
              options={supplierCounterParties}
              selectedOptions={formData?.Seller_Id || null}
              handleSelectChange={(filterOn, newValue) => handleChange(null, "Seller_Id", newValue)}
            />
          </Grid>

          <Grid size={{ xs: 3 }}>
            <VirtualizedAutocomplete
              isLoading={isListDataLoading}
              isMultiple={false}
              isObject={true}
              isWritable={isWritable}
              isLocked={Boolean(isPeriodLocked && isFieldLocked("Buyer_Id", "Deal Header", "Pre-Shipment", lockedFields))}
              filterOn="Customer Name *"
              options={customerCounterParties}
              selectedOptions={formData?.Buyer_Id || null}
              handleSelectChange={(filterOn, newValue) => handleChange(null, "Buyer_Id", newValue)}
            />
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              label="Customer Address *"
              id="Customer_Address"
              disabled={Boolean(isPeriodLocked && isFieldLocked("Customer_Address", "Selling Leg", "Pre-Shipment", lockedFields))}
              value={sellingLegFormData?.Customer_Address || ""}
              onChange={(event) => handleChange(event, "Customer_Address", event.target.value)}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean(isPeriodLocked && isFieldLocked("Customer_Address", "Deal Header", "Pre-Shipment", lockedFields)) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),

                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 3 }}>
            <VirtualizedAutocomplete
              isMultiple={false}
              isObject={false}
              isWritable={isWritable}
              isLocked={Boolean(isPeriodLocked && isFieldLocked("Ship_To_Party_Same_As_Customer", "Selling Leg", "Pre-Shipment", lockedFields))}
              filterOn="Ship to Party"
              options={["Yes", "No"]}
              selectedOptions={sellingLegFormData?.Ship_To_Party_Same_As_Customer || null}
              handleSelectChange={(filterOn, newValue) => handleChange(null, "Ship_To_Party_Same_As_Customer", newValue)}
            />
          </Grid>

          <>
            <Grid size={{ xs: 3 }}>
              <VirtualizedAutocomplete
                isLoading={isListDataLoading}
                isMultiple={false}
                isObject={true}
                isDisabled={sellingLegFormData?.Ship_To_Party_Same_As_Customer !== "No"}
                isWritable={isWritable && sellingLegFormData?.Ship_To_Party_Same_As_Customer === "No"}
                isLocked={Boolean(isPeriodLocked && isFieldLocked("Ship_To_Party_Id", "Selling Leg", "Pre-Shipment", lockedFields))}
                filterOn="Ship to Party"
                options={customerCounterParties}
                selectedOptions={sellingLegFormData?.Ship_To_Party_Id || null}
                handleSelectChange={(filterOn, newValue) => handleChange(null, "Ship_To_Party_Id", newValue)}
              />
            </Grid>

            <Grid size={{ xs: 3 }} className="form-text-field">
              <TextField
                label="Ship to Party Address"
                id="Ship_To_Party_Address"
                disabled={Boolean(isPeriodLocked && isFieldLocked("Ship_To_Party_Address", "Selling Leg", "Pre-Shipment", lockedFields)) || sellingLegFormData?.Ship_To_Party_Same_As_Customer !== "No"}
                value={sellingLegFormData?.Ship_To_Party_Address || ""}
                onChange={(event) => handleChange(event, "Ship_To_Party_Address", event.target.value)}
                size="small"
                slotProps={{
                  input: {
                    readOnly: !isWritable || sellingLegFormData?.Ship_To_Party_Same_As_Customer !== "No",
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean(isPeriodLocked && isFieldLocked("Ship_To_Party_Address", "Selling Leg", "Pre-Shipment", lockedFields)) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }}
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
          </>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                className="date-picker w-100"
                label="Deal Open Month"
                openTo="month"
                views={["month", "year"]}
                inputFormat="MMM-YY"
                disableMaskedInput
                readOnly={!isWritable || (Boolean(isPeriodLocked && isFieldLocked("Booked_for_Month", "Deal Header", "Pre-Shipment", lockedFields)))}
                value={convertToDayJsObject(formData?.Booked_for_Month)}
                onChange={(newDate) => handleChange(null, "Booked_for_Month", newDate)}
                onAccept={(newDate) => handleChange(null, "Booked_for_Month", newDate)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {(Boolean(isPeriodLocked && isFieldLocked("Booked_for_Month", "Deal Header", "Pre-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Deal Open Date"
                disableMaskedInput
                readOnly={!isWritable || (Boolean(isPeriodLocked && isFieldLocked("Order_Date", "Deal Header", "Pre-Shipment", lockedFields)))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Order_Date)}
                onChange={(newDate) => handleChange(null, "Order_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Order_Date", newDate, true)}
                shouldDisableMonth={(month) => shouldDisableDate(month, "Month")}
                shouldDisableYear={(year) => shouldDisableDate(year, "Year")}
                InputProps={{
                  required: formData?.Order_Status?.value === "Closed"
                }}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {(Boolean(isPeriodLocked && isFieldLocked("Order_Date", "Deal Header", "Pre-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Deal Close Date"
                className="date-picker w-100"
                disableMaskedInput
                readOnly={!isWritable || (Boolean(isPeriodLocked && isFieldLocked("Deal_Close_Date", "Deal Header", "Pre-Shipment", lockedFields)))}
                minDate={convertToDayJsObject(formData?.Order_Date)}
                value={convertToDayJsObject(formData?.Deal_Close_Date)}
                onChange={(newDate) => handleChange(null, "Deal_Close_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Deal_Close_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean(isPeriodLocked && isFieldLocked("Deal_Close_Date", "Deal Header", "Pre-Shipment", lockedFields)) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDateTimePicker
                inputFormat="MM/DD/YYYY hh:mm A"
                label="Deal Sheet Approval Date & Time"
                className="datetime-picker w-100"
                disableMaskedInput
                readOnly={!isWritable || (Boolean(isPeriodLocked && isFieldLocked("Deal_Sheet_Approval_Date", "Deal Header", "Pre-Shipment", lockedFields)))}
                value={formData?.Deal_Sheet_Approval_Date ? dayjs(formData?.Deal_Sheet_Approval_Date) : null}
                onChange={(newDateTime) => handleChange(null, "Deal_Sheet_Approval_Date", newDateTime)}
                onAccept={(newDateTime) => handleChange(null, "Deal_Sheet_Approval_Date", newDateTime)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean(isPeriodLocked && isFieldLocked("Deal_Sheet_Approval_Date", "Deal Header", "Pre-Shipment", lockedFields)) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Shipment Month & Date"
                className="date-picker w-100"
                disableMaskedInput
                readOnly={!isWritable || (Boolean(isPeriodLocked && isFieldLocked("Shipment_Month_And_Date", "Deal Header", "Pre-Shipment", lockedFields)))}
                value={convertToDayJsObject(formData?.Shipment_Month_And_Date)}
                onChange={(newDate) => handleChange(null, "Shipment_Month_And_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Shipment_Month_And_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {(Boolean(isPeriodLocked && isFieldLocked("Shipment_Month_And_Date", "Deal Header", "Pre-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }}>
            <VirtualizedAutocomplete
              isMultiple={false}
              isObject={false}
              isWritable={isWritable}
              isLocked={Boolean(isPeriodLocked && isFieldLocked("Trade_Finance_Deal", "Deal Header", "Pre-Shipment", lockedFields))}
              filterOn="Trade Finance Deal"
              options={tradeFinanceDealOptions}
              selectedOptions={formData?.Trade_Finance_Deal || null}
              handleSelectChange={(filterOn, newValue) => handleChange(null, "Trade_Finance_Deal", newValue)}
            />
          </Grid>

          {formData?.Order_Status === "Cancelled" &&
            <Grid size={{ xs: 4 }} className="form-text-field">
              <TextField
                label="Deal Cancelled Reason"
                id="Deal_Cancelled_Reason"
                disabled={Boolean(isPeriodLocked && isFieldLocked("Deal_Cancelled_Reason", "Deal Header", "Pre-Shipment", lockedFields))}
                multiline
                rows={3}
                value={formData?.Deal_Cancelled_Reason || ""}
                size="small"
                required
                slotProps={{
                  input: {
                    readOnly: !isWritable,
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                        {Boolean(isPeriodLocked && isFieldLocked("Deal_Cancelled_Reason", "Deal Header", "Pre-Shipment", lockedFields)) && (
                          <Tooltip
                            title="This field is locked and cannot be edited due to period lock."
                            arrow
                            placement="top"
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ cursor: 'default' }} // No margins
                            />
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
                onChange={(event) => handleChange(event, "Deal_Cancelled_Reason", event.target.value)}
              />
            </Grid>
          }
        </Grid>
      )
    },
    {
      id: 2,
      columns: 6,
      title: `${isSBLCDeal ? "SBLC" : "Product"} Details`,
      content: (
        <>
          {isSBLCDeal ? (
            <DealSBLC
              isWritable={isWritable}
              isLocked={Boolean(isPeriodLocked && isFieldLocked("Seller_Id", "Deal Header", "Pre-Shipment", lockedFields))}
              isListDataLoading={isListDataLoading}
              isPFIAccordionExpanded={isPFIAccordionExpanded}
              isProductGradeAccordionExpanded={isProductGradeAccordionExpanded}
              selectedTab="Deal Header"
              sblcFormData={sblcFormData}
              pfiAccordionRefs={pfiAccordionRefs}
              productGradeAccordionRefs={productGradeAccordionRefs}
              setIsPFIAccordionExpanded={setIsPFIAccordionExpanded}
              setIsProductGradeAccordionExpanded={setIsProductGradeAccordionExpanded}
              setFormData={setFormData}
              setSBLCFormData={setSBLCFormData}
              setShowAlert={setShowAlert}
              setAlertMessage={setAlertMessage}
              setAlertType={setAlertType}
              subTabName={"SBLC Product Grade"}
              tabName={"Pre-Shipment"}
              isPeriodLocked={isPeriodLocked}
              lockedFields={lockedFields}
            />
          ) : formData?.products?.map((product, index) => {
            const productValue = product?.Product_Id?.value || `Product ${index + 1}`;

            return (
              <Accordion
                ref={(element) => (productAccordionRefs.current[index] = element)}
                key={index}
                expanded={isProductAccordionExpanded === index}
                onChange={handleAccordionChange(index)}
              >
                <AccordionSummary
                  id={`product-header-${index}`}
                  aria-controls={`product-content-${index}`}
                  expandIcon={<ExpandMoreIcon />}
                  slot="div"
                >
                  <small className="page-heading fw-bold item">
                    {productValue}
                  </small>

                  <IconButton
                    color="error"
                    size="small"
                    className="pt-0"
                    disabled={!isWritable || (Boolean(isPeriodLocked && isFieldLocked("Product_Id", "Product", "Pre-Shipment", lockedFields)))}
                    onClick={(event) => handleDeleteProduct(event, index)}
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid
                    container
                    spacing={"10px"}
                    className="align-items-start autocomplete"
                  >
                    <Grid size={{ xs: 6 }}>
                      <VirtualizedAutocomplete
                        isLoading={isListDataLoading}
                        isMultiple={false}
                        isObject={true}
                        isWritable={isWritable}
                        isLocked={Boolean(isPeriodLocked && isFieldLocked("Product_Id", "Product", "Pre-Shipment", lockedFields))}
                        isRequired={true}
                        filterOn="Product"
                        options={products}
                        selectedOptions={product.Product_Id || null}
                        handleSelectChange={(filterOn, newValue) => handleProductChange(index, "Product_Id", newValue)}
                      />
                    </Grid>

                    <Grid size={{ xs: 6 }} className="form-text-field">
                      <TextField
                        label="Unit of Measurement *"
                        id="Unit_of_Measurement"
                        disabled
                        value={product?.Product_Id
                          ? productData?.find((_product) => _product?.id === product?.Product_Id?.id)?.Unit_of_Measurement || ""
                          : ""
                        }
                        slotProps={{
                          inputLabel: {
                            shrink:
                              product?.Product_Id &&
                              productData?.some((_product) => _product.id === product.Product_Id.id && _product.Unit_of_Measurement)
                          }
                        }}
                      />
                    </Grid>

                    {isFoodsOrPolymersDeal && (
                      <Grid size={{ xs: 6 }}>
                        <VirtualizedAutocomplete
                          isLoading={isListDataLoading}
                          isMultiple={false}
                          isObject={true}
                          isWritable={isWritable}
                          isLocked={Boolean(isPeriodLocked && isFieldLocked("Product_Grade_Id", "Product Grade", "Pre-Shipment", lockedFields))}
                          filterOn="Product Grade"
                          options={productGrades}
                          selectedOptions={product.Product_Grade_Id || null}
                          handleSelectChange={(filterOn, newValue) => handleProductChange(index, "Product_Grade_Id", newValue)}
                        />
                      </Grid>
                    )}

                    <Grid size={{ xs: 6 }} className="form-text-field">
                      <TextField
                        label="Quantity *"
                        id="Quantity"
                        disabled={Boolean(isPeriodLocked && isFieldLocked("Quantity", "Deal Header", "Pre-Shipment", lockedFields))}
                        type="number"
                        value={product.Quantity || ""}
                        onChange={(event) => handleProductChange(index, "Quantity", event.target.value)}
                        size="small"
                        slotProps={{
                          input: {
                            readOnly: !isWritable,
                            min: 0,
                            max: 2147483647,
                            step: "0.001",
                            endAdornment: (
                              <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                                {Boolean(isPeriodLocked && isFieldLocked("Quantity", "Deal Header", "Pre-Shipment", lockedFields)) && (
                                  <Tooltip
                                    title="This field is locked and cannot be edited due to period lock."
                                    arrow
                                    placement="top"
                                  >
                                    <InfoIcon
                                      fontSize="small"
                                      sx={{ cursor: 'default' }} // No margins
                                    />
                                  </Tooltip>
                                )}
                              </InputAdornment>
                            ),
                          },
                        }}
                        onKeyDown={(event) => {
                          const invalidKeys = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
                          if (invalidKeys.includes(event.key)) {
                            event.preventDefault();
                          }
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 6 }} className="form-text-field">
                      <TextField
                        label="Selling Price *"
                        id="Selling_Price"
                        disabled={Boolean(isPeriodLocked && isFieldLocked("Selling_Price", "Deal Header", "Pre-Shipment", lockedFields))}
                        type="number"
                        value={product.Selling_Price || ""}
                        onChange={(event) => handleProductChange(index, "Selling_Price", event.target.value)}
                        size="small"
                        slotProps={{
                          input: {
                            readOnly: !isWritable,
                            min: 0,
                            max: 2147483647,
                            step: "0.001",
                            endAdornment: (
                              <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                                {Boolean(isPeriodLocked && isFieldLocked("Selling_Price", "Deal Header", "Pre-Shipment", lockedFields)) && (
                                  <Tooltip
                                    title="This field is locked and cannot be edited due to period lock."
                                    arrow
                                    placement="top"
                                  >
                                    <InfoIcon
                                      fontSize="small"
                                      sx={{ cursor: 'default' }}
                                    />
                                  </Tooltip>
                                )}
                              </InputAdornment>
                            ),
                          },
                        }}
                        onKeyDown={(event) => {
                          const invalidKeys = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
                          if (invalidKeys.includes(event.key)) {
                            event.preventDefault();
                          }
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 6 }} className="form-text-field">
                      <TextField
                        label="Buying Price *"
                        id="Buying_Price"
                        disabled={Boolean(isPeriodLocked && isFieldLocked("Buying_Price", "Deal Header", "Pre-Shipment", lockedFields))}
                        type="number"
                        value={product.Buying_Price || ""}
                        onChange={(event) => handleProductChange(index, "Buying_Price", event.target.value)}
                        size="small"
                        slotProps={{
                          input: {
                            readOnly: !isWritable,
                            min: 0,
                            max: 2147483647,
                            step: "0.001",
                            endAdornment: (
                              <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                                {Boolean(isPeriodLocked && isFieldLocked("Buying_Price", "Deal Header", "Pre-Shipment", lockedFields)) && (
                                  <Tooltip
                                    title="This field is locked and cannot be edited due to period lock."
                                    arrow
                                    placement="top"
                                  >
                                    <InfoIcon
                                      fontSize="small"
                                      sx={{ cursor: 'default' }}
                                    />
                                  </Tooltip>
                                )}
                              </InputAdornment>
                            ),
                          },
                        }}
                        onKeyDown={(event) => {
                          const invalidKeys = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
                          if (invalidKeys.includes(event.key)) {
                            event.preventDefault();
                          }
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 6 }} className="form-text-field">
                      <TextField
                        label="HS Code *"
                        id="HS_Code"
                        disabled={Boolean(isPeriodLocked && isFieldLocked("HS_Code", "Deal Header", "Pre-Shipment", lockedFields))}
                        name="HS_Code"
                        value={product?.HS_Code ? product?.HS_Code?.toString() : ""}
                        onChange={(event) => handleProductChange(index, "HS_Code", event.target.value)}
                        size="small"
                        slotProps={{
                          input: {
                            readOnly: !isWritable,
                            inputComponent: CustomTextMask,
                            endAdornment: (
                              <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                                {Boolean(isPeriodLocked && isFieldLocked("HS_Code", "Deal Header", "Pre-Shipment", lockedFields)) && (
                                  <Tooltip
                                    title="This field is locked and cannot be edited due to period lock."
                                    arrow
                                    placement="top"
                                  >
                                    <InfoIcon
                                      fontSize="small"
                                      sx={{ cursor: 'default' }} // No margins
                                    />
                                  </Tooltip>
                                )}
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Grid>

                    {isFoodsOrPolymersDeal && (
                      <Grid size={{ xs: 12 }} className="form-text-field">
                        <TextField
                          label="Specification"
                          id="Product_Specification"
                          disabled={Boolean(isPeriodLocked && isFieldLocked("Product_Specification", "Deal Header", "Pre-Shipment", lockedFields))}
                          multiline
                          rows={3}
                          value={product.Product_Specification || ""}
                          size="small"
                          slotProps={{
                            input: {
                              readOnly: !isWritable,
                              endAdornment: (
                                <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                                  {Boolean(isPeriodLocked && isFieldLocked("Product_Specification", "Deal Header", "Pre-Shipment", lockedFields)) && (
                                    <Tooltip
                                      title="This field is locked and cannot be edited due to period lock."
                                      arrow
                                      placement="top"
                                    >
                                      <InfoIcon
                                        fontSize="small"
                                        sx={{ cursor: 'default' }} // No margins
                                      />
                                    </Tooltip>
                                  )}
                                </InputAdornment>
                              ),
                            },
                          }}
                          onChange={(event) => handleProductChange(index, "Product_Specification", event.target.value)}
                        />
                      </Grid>
                    )}

                    <Grid size={{ xs: 12 }} className="form-text-field">
                      <TextField
                        label="Description *"
                        id="Product_Description"
                        disabled={Boolean(isPeriodLocked && isFieldLocked("Product_Description", "Deal Header", "Pre-Shipment", lockedFields))}
                        multiline
                        rows={3}
                        value={product.Product_Description || ""}
                        size="small"
                        slotProps={{
                          input: {
                            readOnly: !isWritable,
                            endAdornment: (
                              <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                                {Boolean(isPeriodLocked && isFieldLocked("Product_Description", "Deal Header", "Pre-Shipment", lockedFields)) && (
                                  <Tooltip
                                    title="This field is locked and cannot be edited due to period lock."
                                    arrow
                                    placement="top"
                                  >
                                    <InfoIcon
                                      fontSize="small"
                                      sx={{ cursor: 'default' }} // No margins
                                    />
                                  </Tooltip>
                                )}
                              </InputAdornment>
                            ),
                          },
                        }}
                        onChange={(event) => handleProductChange(index, "Product_Description", event.target.value)}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
      )
    },
    {
      id: 3,
      columns: 6,
      title: "PFI Freight And Discount Adjustments",
      content: (
        <Grid
          container
          spacing={"10px"}
          className="w-100 align-items-start"
        >
          <Grid size={{ xs: 6 }} className="form-text-field">
            <TextField
              label="Total Amount"
              id="Total_Amount"
              type="number"
              value={formData?.Total_Amount || ""}
              disabled
              slotProps={{
                inputLabel: { shrink: formData?.Total_Amount }
              }}
            />
          </Grid>

          <Grid size={{ xs: 6 }} className="form-text-field">
            <TextField
              inputRef={freightInputRef}
              label="Freight"
              disabled={Boolean(isPeriodLocked && isFieldLocked("Freight", "Deal Header", "Pre-Shipment", lockedFields))}
              id="Freight"
              type="number"
              value={formData?.Freight || ""}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  step: "0.001",
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean(isPeriodLocked && isFieldLocked("Freight", "Deal Header", "Pre-Shipment", lockedFields)) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),

                },
              }}
              onKeyDown={(event) => {
                const invalidKeys = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
                if (invalidKeys.includes(event.key)) {
                  event.preventDefault();
                }
              }}
              onChange={(event) => handleChange(event, "Freight", event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 6 }} className="form-text-field">
            <TextField
              inputRef={fobValueInputRef}
              label="FOB Value"
              disabled={Boolean(isPeriodLocked && isFieldLocked("FOB_Value", "Deal Header", "Pre-Shipment", lockedFields))}
              id="FOB_Value"
              value={formData?.FOB_Value || ""}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  step: "0.001",
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean(isPeriodLocked && isFieldLocked("FOB_Value", "Deal Header", "Pre-Shipment", lockedFields)) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),

                },
              }}
              onChange={(event) => handleChange(event, "FOB_Value", event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 6 }} className="form-text-field">
            <TextField
              label="Label"
              id="Label"
              disabled={Boolean(isPeriodLocked && isFieldLocked("Label", "Deal Header", "Pre-Shipment", lockedFields))}
              value={formData?.Label || ""}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean(isPeriodLocked && isFieldLocked("Label", "Deal Header", "Pre-Shipment", lockedFields)) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),

                },
              }}
              onChange={(event) => handleChange(event, "Label", event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 6 }} className="form-text-field">
            <TextField
              label="Value"
              id="Value"
              disabled={Boolean(isPeriodLocked && isFieldLocked("Value", "Deal Header", "Pre-Shipment", lockedFields))}
              type="number"
              value={formData?.Value || ""}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  step: "0.001",
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean(isPeriodLocked && isFieldLocked("Value", "Deal Header", "Pre-Shipment", lockedFields)) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),

                },
              }}
              onKeyDown={(event) => {
                const invalidKeys = ["e", "E", "+", "-", "ArrowUp", "ArrowDown"];
                if (invalidKeys.includes(event.key)) {
                  event.preventDefault();
                }
              }}
              onChange={(event) => handleChange(event, "Value", event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <VirtualizedAutocomplete
              isMultiple={false}
              isObject={false}
              isWritable={isWritable}
              isLocked={Boolean(isPeriodLocked && isFieldLocked("Transaction_Type", "Deal Header", "Pre-Shipment", lockedFields))}
              filterOn="Transaction Type"
              options={transactionTypes}
              selectedOptions={formData?.Transaction_Type || null}
              handleSelectChange={(filterOn, newValue) => handleChange(null, "Transaction_Type", newValue)}
            />
          </Grid>
        </Grid>
      )
    }
  ];

  return (
    <form
      ref={formRef}
      className="deal-form mt-1"
      onSubmit={handleSubmit}
    >
      <Grid
        container
        spacing={1}
        className="w-100 align-items-start autocomplete gap-1"
      >
        {formSectionsHTML.map((section, index) => (
          <Grid
            key={index}
            size={{ xs: section?.columns }}
          >
            <Accordion
              key={section.id}
              expanded={expandedFormSections.includes(section.id)}
              onChange={handleFormSectionAccordionChange(section.id)}
            >
              <AccordionSummary
                id="panel-header"
                aria-controls="panel-content"
                expandIcon={<ExpandMoreIcon />}
                slot="div"
              >
                <small className="page-heading title">
                  {section.title}
                </small>

                {(section.title === "Product Details" && expandedFormSections.includes(section.id)) && (
                  <IconButton
                    color="primary"
                    disabled={!isWritable || (Boolean(isPeriodLocked))}
                    onClick={handleAddProduct}
                  >
                    <AddBoxIcon
                      fontSize="large"
                      disabled={isPeriodLocked}
                      sx={{ color: '#0C969C' }}
                    />
                  </IconButton>
                )}
              </AccordionSummary>
              <AccordionDetails>
                {section.content}
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>
    </form>
  );
});

export default memo(DealHeader);